/**
 * "The Crossing" — cross a street under a 3-segment pendulum that swings in 3D
 * and slowly precesses at its mount. The heavy bob traces a deterministic,
 * QUASI-PERIODIC path (a learnable rhythm, not true chaos): like a qubit's
 * state precessing on the Bloch sphere, you cross when the PHASE swings the
 * weight clear. A clip knocks you back to the near side (non-lethal).
 * Pure + deterministic: no Babylon/DOM.
 */
export interface CrossingState {
  t: number;
  pos: { x: number; z: number };
  facing: number;
  strideAccum: number;
  /** Bob world position (also drives the rendered chain). */
  bob: { x: number; y: number; z: number };
  /** Swing offsets + precession angle, for rendering the pendulum. */
  swingX: number;
  swingZ: number;
  precession: number;
  /** Horizontal distance from the player to the bob (0..). */
  bobDist: number;
  /** Bob speed (units/s) — drives the whoosh. */
  bobSpeed: number;
  hitFlash: number; // seconds remaining of the "clipped" flash
  exited: boolean;
}

export interface CrossingInput {
  move: { x: number; y: number };
}

export interface CrossingConfig {
  speed: number;
  xMin: number;
  xMax: number;
  zMin: number;
  zMax: number;
  mount: { x: number; y: number; z: number };
  swingRadius: number;
  omegaX: number;
  omegaZ: number;
  omegaPrec: number;
  bobY: number;
  hitRadius: number;
  knockbackZ: number;
  exitZ: number;
  exitHalfW: number;
  playerRadius: number;
  strideLength: number;
}

export type CrossingEvent = { type: 'footstep' } | { type: 'hit' } | { type: 'exit' };

export const DEFAULT_CROSSING_CONFIG: CrossingConfig = {
  speed: 5.2,
  xMin: -6,
  xMax: 6,
  zMin: 0,
  zMax: 24,
  mount: { x: 0, y: 7, z: 12 },
  swingRadius: 4,
  omegaX: 0.6,
  omegaZ: 0.42,
  omegaPrec: 0.15,
  bobY: 1.7,
  hitRadius: 1.35,
  knockbackZ: 7,
  exitZ: 20,
  exitHalfW: 2.4,
  playerRadius: 0.5,
  strideLength: 1.4,
};

export function createCrossingState(): CrossingState {
  return {
    t: 0,
    pos: { x: 0, z: 3 },
    facing: 0,
    strideAccum: 0,
    bob: { x: 0, y: 1.7, z: 12 },
    swingX: 0,
    swingZ: 0,
    precession: 0,
    bobDist: 99,
    bobSpeed: 0,
    exited: false,
    hitFlash: 0,
  };
}

/** Deterministic bob position at time t (also used by the renderer). */
export function bobAt(
  t: number,
  cfg: CrossingConfig,
): { x: number; y: number; z: number; sx: number; sz: number; prec: number } {
  const sx = cfg.swingRadius * Math.sin(t * cfg.omegaX);
  const sz = cfg.swingRadius * 0.6 * Math.sin(t * cfg.omegaZ + 1.0);
  const prec = t * cfg.omegaPrec;
  const cos = Math.cos(prec);
  const sin = Math.sin(prec);
  const x = cfg.mount.x + cos * sx - sin * sz;
  const z = cfg.mount.z + sin * sx + cos * sz;
  // A gentle vertical bob so the weight visibly dips as it sweeps.
  const off = Math.hypot(sx, sz) / cfg.swingRadius;
  const y = cfg.bobY + 0.5 * (1 - off);
  return { x, y, z, sx, sz, prec };
}

export function stepCrossing(
  s: CrossingState,
  input: CrossingInput,
  dt: number,
  cfg: CrossingConfig,
): CrossingEvent[] {
  const events: CrossingEvent[] = [];
  s.t += dt;
  if (s.hitFlash > 0) s.hitFlash = Math.max(0, s.hitFlash - dt);
  if (s.exited) return events;

  // Move (up on the stick = forward).
  const mx = input.move.x;
  const mz = -input.move.y;
  const len = Math.hypot(mx, mz);
  const px = s.pos.x;
  const pz = s.pos.z;
  if (len > 0.01) {
    s.facing = Math.atan2(mx, mz);
    s.pos.x += (mx / len) * cfg.speed * dt * Math.min(1, len);
    s.pos.z += (mz / len) * cfg.speed * dt * Math.min(1, len);
  }
  const r = cfg.playerRadius;
  s.pos.x = Math.max(cfg.xMin + r, Math.min(cfg.xMax - r, s.pos.x));
  s.pos.z = Math.max(cfg.zMin + r, Math.min(cfg.zMax - r, s.pos.z));

  // Bob (+ its speed, for the whoosh).
  const b = bobAt(s.t, cfg);
  const bPrev = bobAt(s.t - dt, cfg);
  s.bob.x = b.x;
  s.bob.y = b.y;
  s.bob.z = b.z;
  s.swingX = b.sx;
  s.swingZ = b.sz;
  s.precession = b.prec;
  s.bobSpeed = Math.hypot(b.x - bPrev.x, b.y - bPrev.y, b.z - bPrev.z) / dt;
  s.bobDist = Math.hypot(s.pos.x - b.x, s.pos.z - b.z);

  // Footsteps.
  const moved = Math.hypot(s.pos.x - px, s.pos.z - pz);
  s.strideAccum += moved;
  while (s.strideAccum >= cfg.strideLength) {
    s.strideAccum -= cfg.strideLength;
    events.push({ type: 'footstep' });
  }

  // Clipped by the bob → a stumble back (not a full reset).
  if (s.bobDist < cfg.hitRadius && s.hitFlash <= 0) {
    s.pos.z = Math.max(cfg.knockbackZ, s.pos.z - 4);
    s.hitFlash = 0.6;
    events.push({ type: 'hit' });
    return events;
  }

  // Crossed.
  if (s.pos.z >= cfg.exitZ && Math.abs(s.pos.x - cfg.mount.x) <= cfg.exitHalfW) {
    s.exited = true;
    events.push({ type: 'exit' });
  }

  return events;
}
